import { getInitials } from '@/lib/utils'

const GRADIENTS = [
  'var(--identity-gradient-1)',
  'var(--identity-gradient-2)',
  'var(--identity-gradient-3)',
  'var(--identity-gradient-4)',
  'var(--identity-gradient-5)',
]

// Variant ratios encode the design intent: contacts feel rounder and more
// profile-like; companies feel squarer and more logo-like. Add new variants
// here if vendors / teams / etc. warrant distinct visual treatment in the
// future.
const VARIANTS = {
  contact: { borderRadiusRatio: 0.28, fontSizeRatio: 0.33 },
  company: { borderRadiusRatio: 0.25, fontSizeRatio: 0.36 },
} as const

export default function Avatar({
  name,
  size = 36,
  variant = 'contact',
  online,
  ringColor = 'var(--surface)',
}: {
  name: string
  size?: number
  variant?: keyof typeof VARIANTS
  // Optional presence dot. undefined/false → no dot (backward-compatible with
  // every existing call site). true → a small online dot at bottom-right.
  online?: boolean
  // Color of the ring around the dot — should match the background the avatar
  // sits on so the dot reads on any surface. Defaults to the panel surface.
  ringColor?: string
}) {
  const { borderRadiusRatio, fontSizeRatio } = VARIANTS[variant]
  const idx = (name?.charCodeAt(0) || 0) % GRADIENTS.length
  const dotSize = Math.max(7, Math.round(size * 0.3))
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <div style={{
        width: size,
        height: size,
        borderRadius: size * borderRadiusRatio,
        background: GRADIENTS[idx],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * fontSizeRatio,
        fontWeight: 700,
        color: 'var(--accent-ink)',
      }}>
        {getInitials(name)}
      </div>
      {online && (
        <span
          aria-label="Online"
          style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: dotSize,
            height: dotSize,
            borderRadius: '50%',
            background: 'var(--positive)',
            border: `2px solid ${ringColor}`,
            boxSizing: 'border-box',
          }}
        />
      )}
    </div>
  )
}
